import useEmblaCarousel, { UseEmblaCarouselType } from 'embla-carousel-react';
import _ from 'lodash';
import { ChevronLeftIcon, ChevronRightIcon } from 'lucide-react';
import { action, makeObservable, observable } from 'mobx';
import { observer } from 'mobx-react-lite';
import React, { createContext, useCallback, useContext, useEffect, useMemo } from 'react';
import { twMerge } from 'tailwind-merge';
import { useMobxInstance } from '../../utils/mobxUtil';

class CarouselStore {
    /**
     * 현재 슬라이드 인덱스
     */
    currentSlideIndex = 0;
    /**
     * 화면에 보이는 슬라이드들
     * - 현재 슬라이드
     * - 인접한 슬라이드 (emblaApi 의 slidesInView 에 포함된 슬라이드)
     * - 한 번 렌더링된 슬라이드
     */
    inViewSlides = new Set<number>();
    /**
     * emblaApi 의 scrollSnapList
     */
    scrollSnaps: number[] = [];
    /**
     * 본문 높이에 따라 자동으로 높이를 조정할지 여부
     */
    autoHeight = false;
    /**
     * 컨테이너 높이
     * - autoHeight 가 true 인 경우에만 사용됨
     */
    containerHeight?: number;

    constructor(autoHeight: boolean = false) {
        this.autoHeight = autoHeight;

        makeObservable(this, {
            currentSlideIndex: observable,
            inViewSlides: observable,
            containerHeight: observable,
            autoHeight: observable,
            scrollSnaps: observable.struct,

            updateCurrentSlide: action,
            updateInViewSlides: action,
            setContainerHeight: action,
            setScrollSnaps: action,
        });
    }

    updateCurrentSlide(index: number) {
        this.currentSlideIndex = index;
    }

    /**
     * 화면에 보이는 슬라이드들을 업데이트
     * - 한 번 추가된 슬라이드는 이미 렌더링 되었으므로 굳이 별도로 제거하지 않음
     */
    updateInViewSlides(indexes: number[]) {
        _.forEach(indexes, (index) => {
            this.inViewSlides.add(index);
        });
    }

    setContainerHeight(height: number) {
        this.containerHeight = height;
    }

    setScrollSnaps(scrollSnaps: number[]) {
        this.scrollSnaps = scrollSnaps;
    }

    isCurrentSlide(index: number) {
        return this.currentSlideIndex === index;
    }

    isInViewSlide(index: number) {
        return this.inViewSlides.has(index);
    }
}

type CarouselContextType = {
    emblaRef: UseEmblaCarouselType[0];
    emblaApi: UseEmblaCarouselType[1];
    carouselStore: CarouselStore;
};

export const CarouselContext = createContext<CarouselContextType | null>(null);

/**
 * 캐러셀 프로바이더
 * - embla-carousel의 컨텍스트를 제공
 * - 슬라이드 lazy loading을 위한 inView 상태 관리
 * - 스와이프와 외부 상태(예: URL 파라미터) 동기화 지원
 */
export const CarouselProvider = observer(
    ({
        children,
        emblaOptions,
        onSlideChange,
        currentSlideIndex: currentSlideIndexProp,
        autoHeight = false,
    }: {
        children: React.ReactNode;
        emblaOptions?: Parameters<typeof useEmblaCarousel>[0];
        /**
         * 슬라이드 변경 시 호출되는 콜백 함수
         * */
        onSlideChange?: (slideIndex: number) => void;
        /**
         * 외부에서 설정하는 현재 슬라이드 인덱스 (URL 동기화 등에 사용)
         * */
        currentSlideIndex?: number;
        /**
         * 본문 높이에 따라 자동으로 높이를 조정할지 여부
         */
        autoHeight?: boolean;
    }) => {
        const [emblaRef, emblaApi] = useEmblaCarousel({
            ...emblaOptions,
            watchDrag: (emblaApi, e) => {
                const optionWatchDragAction = _.isFunction(emblaOptions?.watchDrag)
                    ? emblaOptions?.watchDrag?.(emblaApi, e)
                    : true;

                if (optionWatchDragAction === false) {
                    return false;
                }

                const event = e as TouchEvent;

                // 캐러셀 무시 여부
                const isIgnoreCarousel = !!(event.target as HTMLElement)?.closest(
                    '[data-ignore-carousel]',
                );
                if (isIgnoreCarousel) {
                    return false;
                }

                const touches = (e as TouchEvent).touches;
                // iOS 에서 백스와이프 동작을 허용하기 위해, 화면 왼쪽 가장자리 20px 영역에서 터치 시 캐러셀 드래그 비활성시킴
                if (touches && touches.length === 1 && touches[0].clientX <= 20) {
                    // false 가 기본 동작을 막는 것임
                    return false;
                }

                return true;
            },
        });
        const carouselStore = useMobxInstance(CarouselStore, autoHeight);

        // 슬라이드 선택 시 상태 업데이트 및 외부 콜백 호출
        const updateCurrentSlide = useCallback(() => {
            if (!emblaApi) {
                return;
            }

            const selectedIndex = emblaApi.selectedScrollSnap();
            carouselStore.updateCurrentSlide(selectedIndex);

            if (onSlideChange) {
                onSlideChange(selectedIndex);
            }
        }, [emblaApi, onSlideChange, carouselStore]);

        // 화면에 보이는 슬라이드들을 추적하여 필요한 슬라이드만 렌더링
        const updateInViewSlides = useCallback(() => {
            if (!emblaApi) {
                return;
            }

            const inView = emblaApi.slidesInView(); // 현재 화면에 보이는 슬라이드들
            const selectedIndex = emblaApi.selectedScrollSnap(); // 현재 선택된 슬라이드

            carouselStore.updateInViewSlides([...inView, selectedIndex]);
        }, [emblaApi, carouselStore]);

        const updateScrollSnaps = useCallback(() => {
            if (!emblaApi) {
                return;
            }
            carouselStore.setScrollSnaps(emblaApi.scrollSnapList());
        }, [emblaApi]);

        // 외부에서 currentSlideIndex가 변경되면 해당 슬라이드로 이동 (URL 동기화 등)
        // [순서주의] 아래 초기화 이벤트 리스너가 등록되기 전에 호출되어야 함
        useEffect(() => {
            if (!emblaApi || currentSlideIndexProp === undefined) {
                return;
            }

            if (!carouselStore.isCurrentSlide(currentSlideIndexProp)) {
                // 애니메이션 없이 이동
                emblaApi.scrollTo(currentSlideIndexProp, true);
            }
        }, [emblaApi, currentSlideIndexProp]);

        useEffect(() => {
            if (!emblaApi) {
                return;
            }

            updateCurrentSlide();
            updateInViewSlides();
            updateScrollSnaps();

            // 이벤트 리스너 등록
            emblaApi.on('select', updateCurrentSlide); // 슬라이드 선택 시 외부 콜백 호출
            emblaApi.on('slidesInView', updateInViewSlides); // 화면에 보이는 슬라이드 변경 시 업데이트
            emblaApi.on('reInit', updateScrollSnaps);

            return () => {
                emblaApi.off('select', updateCurrentSlide);
                emblaApi.off('slidesInView', updateInViewSlides);
                emblaApi.off('reInit', updateScrollSnaps);
            };
        }, [emblaApi, updateCurrentSlide, updateInViewSlides]);

        // Context value를 memoization으로 안정적인 참조 유지
        const contextValue = useMemo(
            () => ({
                emblaRef,
                emblaApi,
                carouselStore,
            }),
            [emblaRef, emblaApi, carouselStore],
        );

        return <CarouselContext.Provider value={contextValue}>{children}</CarouselContext.Provider>;
    },
);

export const useCarouselContext = () => {
    const context = useContext(CarouselContext);

    if (!context) {
        throw new Error('useCarouselContext must be used within a CarouselProvider');
    }

    return context;
};

/**
 * 컨테이너 바로 아래에 슬라이더가 있어야 함
 * - 슬라이더 간 여백을 주고 싶을 땐 Container 컴포넌트에 gap-5 같이 클래스 추가
 * - 슬라이더 내부 여백을 주고 싶을 땐 Slide 컴포넌트에 px-5 같이 클래스 추가
 */
const Container = observer(
    ({ children, className }: { children: React.ReactNode; className?: string }) => {
        const { carouselStore } = useCarouselContext();

        // 자동으로 slideIndex를 부여
        const processedChildren = React.Children.map(children, (child, index) => {
            if (!React.isValidElement(child)) {
                return child;
            }

            // Slide 컴포넌트인 경우 slideIndex 자동 부여
            if (child.type === Slide) {
                return React.cloneElement(child, {
                    ...(child.props as any),
                    slideIndex: index,
                } as any);
            }

            return child;
        });

        const containerStyle =
            carouselStore.autoHeight && carouselStore.containerHeight
                ? { height: `${carouselStore.containerHeight}px` }
                : undefined;

        return (
            <div className={twMerge('embla-container', className)} style={containerStyle}>
                {processedChildren}
            </div>
        );
    },
);

/**
 * [중요] 슬라이드는 Container 의 바로 아래에 위치해야 함
 */
const Slide = observer(
    ({
        children,
        className,
        slideIndex,
    }: {
        children: React.ReactNode;
        className?: string;
        /** 슬라이드 인덱스 (Container에서 자동 부여됨) */
        slideIndex?: number;
    }) => {
        const slideRef = React.useRef<HTMLDivElement>(null);

        const { carouselStore } = useCarouselContext();

        const isCurrentSlide = carouselStore.isCurrentSlide(slideIndex!);
        // slideIndex가 inViewSlides에 포함된 경우에만 렌더링
        const shouldRender = carouselStore.isInViewSlide(slideIndex!);

        // autoHeight 모드에서 현재 슬라이드의 높이 측정 및 설정
        useEffect(() => {
            if (!carouselStore.autoHeight || !shouldRender || !isCurrentSlide) {
                return;
            }

            const slideElement = slideRef.current;
            if (!slideElement) {
                return;
            }

            const measureHeight = () => {
                if (slideRef.current) {
                    const height = slideRef.current.scrollHeight;
                    carouselStore.setContainerHeight(height);
                }
            };

            // 초기 높이 측정
            requestAnimationFrame(measureHeight);

            // ResizeObserver로 컨텐츠 변경 감지 (비동기 로딩, 이미지 로드 등)
            const resizeObserver = new ResizeObserver(() => {
                measureHeight();
            });

            resizeObserver.observe(slideElement);

            return () => {
                resizeObserver.disconnect();
            };
        }, [isCurrentSlide, slideIndex, shouldRender, carouselStore]);

        return (
            <div
                ref={slideRef}
                className={twMerge('embla-slide', className)}
                data-ignore-pull-to-refresh={true}
            >
                {shouldRender ? children : null}
            </div>
        );
    },
);

const PrevButton = observer(
    ({
        children,
        className,
        onClick,
        disabled,
    }: {
        children: React.ReactNode;
        className?: string;
        /** 이전 슬라이드로 이동 후 호출되는 추가 콜백 */
        onClick?: () => void;
        disabled?: boolean;
    }) => {
        const { emblaApi } = useCarouselContext();

        const scrollPrev = useCallback(() => {
            if (emblaApi) {
                emblaApi.scrollPrev(); // embla API로 이전 슬라이드로 이동
            }
            onClick?.(); // 추가 콜백 실행
        }, [emblaApi, onClick]);

        return (
            <button className={className} onClick={scrollPrev} disabled={disabled}>
                {children}
            </button>
        );
    },
);

const NextButton = observer(
    ({
        children,
        className,
        onClick,
        disabled,
    }: {
        children: React.ReactNode;
        className?: string;
        /** 다음 슬라이드로 이동 후 호출되는 추가 콜백 */
        onClick?: () => void;
        disabled?: boolean;
    }) => {
        const { emblaApi } = useCarouselContext();

        const scrollNext = useCallback(() => {
            if (emblaApi) {
                emblaApi.scrollNext(); // embla API로 다음 슬라이드로 이동
            }
            onClick?.(); // 추가 콜백 실행
        }, [emblaApi, onClick]);

        return (
            <button className={className} onClick={scrollNext} disabled={disabled}>
                {children}
            </button>
        );
    },
);

const PrevCircleButton = observer(
    ({
        className,
        onClick,
        isShow = true,
        size = 'md',
    }: {
        className?: string;
        /** 이전 슬라이드로 이동 후 호출되는 추가 콜백 */
        onClick?: () => void;
        /** 버튼 표시 여부 (false일 경우 opacity-0, pointer-events-none 적용) */
        isShow?: boolean;
        /** 버튼 크기: sm(h-8 w-8 p-1), md(h-10 w-10 p-2), lg(h-12 w-12 p-3) */
        size?: 'sm' | 'md' | 'lg';
    }) => {
        const { carouselStore } = useCarouselContext();

        const sizeClasses = {
            sm: 'h-8 w-8 p-1',
            md: 'h-10 w-10 p-2',
            lg: 'h-12 w-12 p-3',
        };

        const iconSizeClasses = {
            sm: 'h-4 w-4',
            md: 'h-6 w-6',
            lg: 'h-8 w-8',
        };

        // 첫 번째 슬라이드인 경우 비활성화
        const isDisabled = carouselStore.currentSlideIndex === 0;
        // isShow가 false이거나 첫 번째 슬라이드인 경우 숨김
        const shouldHide = !isShow || isDisabled;

        return (
            <PrevButton
                className={twMerge(
                    'border-base-300 bg-base-100 hover:bg-base-200 z-10 flex items-center justify-center rounded-full border shadow-sm transition-opacity duration-200',
                    sizeClasses[size],
                    shouldHide && 'pointer-events-none opacity-0',
                    className,
                )}
                onClick={onClick}
                disabled={isDisabled}
            >
                <ChevronLeftIcon className={iconSizeClasses[size]} />
            </PrevButton>
        );
    },
);

const NextCircleButton = observer(
    ({
        className,
        onClick,
        isShow = true,
        size = 'md',
    }: {
        className?: string;
        /** 다음 슬라이드로 이동 후 호출되는 추가 콜백 */
        onClick?: () => void;
        /** 버튼 표시 여부 (false일 경우 opacity-0, pointer-events-none 적용) */
        isShow?: boolean;
        /** 버튼 크기: sm(h-8 w-8 p-1), md(h-10 w-10 p-2), lg(h-12 w-12 p-3) */
        size?: 'sm' | 'md' | 'lg';
    }) => {
        const { carouselStore } = useCarouselContext();

        const sizeClasses = {
            sm: 'h-8 w-8 p-1',
            md: 'h-10 w-10 p-2',
            lg: 'h-12 w-12 p-3',
        };

        const iconSizeClasses = {
            sm: 'h-4 w-4',
            md: 'h-6 w-6',
            lg: 'h-8 w-8',
        };

        // 마지막 슬라이드인 경우 비활성화
        const isDisabled = carouselStore.currentSlideIndex >= carouselStore.scrollSnaps.length - 1;
        // isShow가 false이거나 마지막 슬라이드인 경우 숨김
        const shouldHide = !isShow || isDisabled;

        return (
            <NextButton
                className={twMerge(
                    'border-base-300 bg-base-100 hover:bg-base-200 z-10 flex items-center justify-center rounded-full border shadow-sm transition-opacity duration-200',
                    sizeClasses[size],
                    shouldHide && 'pointer-events-none opacity-0',
                    className,
                )}
                onClick={onClick}
                disabled={isDisabled}
            >
                <ChevronRightIcon className={iconSizeClasses[size]} />
            </NextButton>
        );
    },
);

const Dots = observer(
    ({
        className,
        dotClassName,
        activeDotClassName,
    }: {
        className?: string;
        /** 각 dot의 클래스명 */
        dotClassName?: string;
        /** 활성화된 dot의 클래스명 */
        activeDotClassName?: string;
    }) => {
        const { emblaApi, carouselStore } = useCarouselContext();

        const scrollTo = useCallback(
            (index: number) => {
                if (emblaApi) {
                    emblaApi.scrollTo(index);
                }
            },
            [emblaApi],
        );

        return (
            <div className={twMerge('flex justify-center gap-2', className)}>
                {carouselStore.scrollSnaps.map((_, index) => (
                    <button
                        key={index}
                        className={twMerge(
                            'h-2 w-2 rounded-full',
                            index === carouselStore.currentSlideIndex
                                ? twMerge('bg-base-content/70', activeDotClassName)
                                : twMerge('bg-base-300', dotClassName),
                        )}
                        onClick={() => scrollTo(index)}
                    />
                ))}
            </div>
        );
    },
);

/**
 * 캐러셀
 * - 캐러셀 컨테이너와 슬라이드를 캐러셀 컴포넌트로 감싸서 사용할 수 있도록 함
 * - 캐러셀 div 는 overflow-hidden 을 가지고 있어야 함
 * - 부모 너비 100% 내부에서 동작
 *
 * [높이 지정]
 * - 부모 높이에 맞추려면 부모를 flex-col 로 설정하고 높이를 지정해주면 됨
 * - 필요한 경우 높이 지정
 *
 * @see https://www.embla-carousel.com/api/events
 */
const Carousel = Object.assign(
    observer(({ children, className }: { children: React.ReactNode; className?: string }) => {
        const { emblaRef } = useCarouselContext();

        return (
            <div className={twMerge('embla select-none', className)} ref={emblaRef}>
                {children}
            </div>
        );
    }),
    {
        /**
         * 캐러셀 컨테이너
         */
        Container: Container,
        /**
         * 캐러셀 슬라이드
         */
        Slide: Slide,
        /**
         * 이전 버튼
         */
        PrevButton: PrevButton,
        /**
         * 다음 버튼
         */
        NextButton: NextButton,
        /**
         * 원형 이전 버튼 (그림자 + 보더 스타일)
         */
        PrevCircleButton: PrevCircleButton,
        /**
         * 원형 다음 버튼 (그림자 + 보더 스타일)
         */
        NextCircleButton: NextCircleButton,
        /**
         * 페이지 인디케이터 (dots)
         */
        Dots: Dots,
    },
);

export default Carousel;
